import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, FileImage, Layers, X } from 'lucide-react';
import useNotificationFeedStore from '../store/useNotificationFeedStore';

const TOAST_VISIBLE_MS = 6000;
const MAX_VISIBLE_TOASTS = 3;

const ICON_BY_TYPE = {
  post: FileImage,
  campaign: Layers,
  profile: Bell,
  subscription: Bell,
  general: Bell,
};

// PushToastStack mirrors new entries in the notification feed store as
// transient on-screen toasts. Each toast is fully clickable — clicking
// it routes to `data.url`, marks the underlying feed item as read, and
// dismisses the toast. Hand-dismiss via the X dismisses only the toast,
// leaving the feed entry untouched so it can still be reviewed under
// /notifications.
const PushToastStack = () => {
  const navigate = useNavigate();
  const items = useNotificationFeedStore((s) => s.items);
  const markRead = useNotificationFeedStore((s) => s.markRead);

  const [toasts, setToasts] = useState([]);
  const seenIdsRef = useRef(new Set());
  const mountedRef = useRef(false);

  useEffect(() => {
    // On first mount, seed the seen set with whatever's already in the
    // feed so we don't replay historical entries as toasts after a hot
    // reload or navigation back into the app.
    if (!mountedRef.current) {
      mountedRef.current = true;
      items.forEach((item) => seenIdsRef.current.add(item.id));
      return;
    }

    const fresh = items.filter((item) => !seenIdsRef.current.has(item.id));
    if (fresh.length === 0) return;
    fresh.forEach((item) => seenIdsRef.current.add(item.id));

    setToasts((prev) => {
      // Newest on top; cap stack height.
      const next = [...fresh.reverse(), ...prev];
      return next.slice(0, MAX_VISIBLE_TOASTS);
    });
  }, [items]);

  useEffect(() => {
    if (toasts.length === 0) return undefined;
    const timers = toasts.map((toast) =>
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== toast.id));
      }, TOAST_VISIBLE_MS),
    );
    return () => timers.forEach(clearTimeout);
  }, [toasts]);

  if (toasts.length === 0) return null;

  const handleClick = (toast) => {
    markRead(toast.id);
    setToasts((prev) => prev.filter((t) => t.id !== toast.id));
    if (toast.url) {
      navigate(toast.url);
    } else {
      navigate('/notifications');
    }
  };

  const handleDismiss = (event, toastId) => {
    event.stopPropagation();
    setToasts((prev) => prev.filter((t) => t.id !== toastId));
  };

  return (
    <div className='pointer-events-none fixed right-4 top-4 z-[9999] flex w-[min(360px,calc(100vw-2rem))] flex-col gap-2'>
      {toasts.map((toast) => {
        const Icon = ICON_BY_TYPE[toast.type] || Bell;
        return (
          <button
            key={toast.id}
            type='button'
            onClick={() => handleClick(toast)}
            className='pointer-events-auto flex w-full items-start gap-3 rounded-xl px-3.5 py-3 text-left shadow-lg transition-transform active:scale-[0.98]'
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            }}
          >
            <span
              className='mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg'
              style={{ background: 'var(--brand)', color: '#fff' }}
            >
              <Icon className='h-4 w-4' />
            </span>
            <span className='min-w-0 flex-1'>
              <span
                className='block text-sm font-semibold leading-snug'
                style={{ color: 'var(--text-primary)' }}
              >
                {toast.title}
              </span>
              {toast.message ? (
                <span
                  className='mt-0.5 block text-xs leading-snug'
                  style={{ color: 'var(--text-body)' }}
                >
                  {toast.message}
                </span>
              ) : null}
              {toast.url ? (
                <span
                  className='mt-1 block text-[11px] font-medium'
                  style={{ color: 'var(--brand)' }}
                >
                  Tap to open
                </span>
              ) : null}
            </span>
            <span
              role='button'
              tabIndex={0}
              aria-label='Dismiss notification'
              onClick={(event) => handleDismiss(event, toast.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  handleDismiss(event, toast.id);
                }
              }}
              className='shrink-0 rounded-md p-1 transition-opacity hover:opacity-100'
              style={{ color: 'var(--text-faint)', opacity: 0.7 }}
            >
              <X className='h-4 w-4' />
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default PushToastStack;
